/**
 * Restricted-editing plugin — replicates PropDocs' CKEditor RestrictedEditingMode.
 * See reference/RESTRICTED-EDITING-SPEC.md.
 *
 * Model: locked-by-default + editable EXCEPTIONS.
 *  - Template context: fully editable; "Make editable" wraps the selection in an
 *    exception range (decorated `.restricted-editing-exception`). Anything can be
 *    marked (word / paragraph(s) / table / cell / checkbox / underscore blank).
 *  - Deal-doc (locked) context: body is read-only EXCEPT inside exception ranges
 *    and content-control (sdt) nodes; a filterTransaction rejects edits elsewhere.
 *
 * Exception ranges are stored in plugin state (as {from,to} mapped across edits)
 * plus any inline `sdt` content controls, which are always editable regions.
 */
import { Plugin, PluginKey, TextSelection, type EditorState } from 'prosemirror-state';
import { CellSelection } from 'prosemirror-tables';
import { Decoration, DecorationSet, type EditorView } from 'prosemirror-view';
import type { Node as PMNode } from 'prosemirror-model';
import { PLACEHOLDER_WRITE_META } from './placeholderSync';

export type RestrictedContext = 'off' | 'template' | 'locked';

export interface RestrictedRange {
  from: number;
  to: number;
}

interface RState {
  context: RestrictedContext;
  ranges: RestrictedRange[];
}

export const restrictedKey = new PluginKey<RState>('propdocsRestricted');

/** Meta actions dispatched to the plugin. */
type Meta =
  | { type: 'setContext'; context: RestrictedContext }
  | { type: 'addRange'; from: number; to: number }
  | { type: 'toggleRange'; from: number; to: number }
  // Marks an authoring transaction (wrap/unwrap an editable region) so the lock
  // lets it through. Carries no state change.
  | { type: 'suppressLock'; value: boolean };

/**
 * Does this transaction consist solely of applying/removing run formatting
 * alongside its `formatChange` marker?
 *
 * Mark steps carry a `mark` we can inspect, so a format-only transaction is
 * identifiable without trusting a meta flag the caller could set for anything.
 */
function isTrackedFormatOnly(
  tr: import('prosemirror-state').Transaction,
  _state: EditorState
): boolean {
  if (tr.steps.length === 0) return false;
  for (const step of tr.steps) {
    const mark = (step as unknown as { mark?: { type?: { name?: string } } }).mark;
    const name = mark?.type?.name;
    if (!name) return false; // a non-mark step (content edit) — not format-only
    if (!STRIKE_EXEMPT_MARKS.has(name)) return false;
  }
  return true;
}

/**
 * The ONLY marks permitted on locked text.
 *
 * Deliberately just strikethrough and its revision marker — not the whole
 * formatting palette. Striking a clause is a reviewable proposal about the
 * text's fate; restyling it is an edit to protected wording. Requiring the
 * `formatChange` marker was too strict: a deal document not in suggesting mode
 * mints no marker, so the strike was still rejected.
 */
const STRIKE_EXEMPT_MARKS = new Set(['strike', 'formatChange']);

/** True if [from,to] overlaps any existing exception range. */
function overlapsRange(ranges: RestrictedRange[], from: number, to: number): boolean {
  return ranges.some((r) => from < r.to && to > r.from);
}

const ZERO_WIDTH = /[\u200b\u200c\u200d\ufeff]/g;
const BLANK = /^[_\s]+$/;

/** Tag of PropDocs placeholder SDTs (matches the registry's PLACEHOLDER_TAG).
 *  Only THESE get click-to-select-all; restricted regions edit normally. */
const PLACEHOLDER_SDT_TAG = 'propdocs:placeholder@1';

/** Is [from,to] fully inside an editable region (an exception range OR an sdt node)? */
function isInsideEditable(state: EditorState, ranges: RestrictedRange[], from: number, to: number): boolean {
  for (const r of ranges) if (from >= r.from && to <= r.to) return true;
  // A cell marked editable is editable THROUGHOUT — that is the point of
  // marking the container: the user may type, clear it, and type again.
  let inEditableCell = false;
  state.doc.nodesBetween(from, Math.max(from, to), (node, pos) => {
    if (
      (node.type.name === 'tableCell' || node.type.name === 'tableHeader') &&
      node.attrs.restrictedEditable === true &&
      from >= pos &&
      to <= pos + node.nodeSize
    ) {
      inEditableCell = true;
    }
  });
  if (inEditableCell) return true;
  // also: fully inside an inline sdt (content control) node
  let inSdt = false;
  state.doc.nodesBetween(from, Math.max(from, to), (node, pos) => {
    if ((node.type.name === 'sdt' || node.type.name === 'blockSdt') && from >= pos && to <= pos + node.nodeSize) {
      inSdt = true;
    }
  });
  return inSdt;
}

/**
 * All editable regions in document order: exception ranges PLUS content-control
 * (sdt) nodes' inner content. Used by Tab to jump between fillable fields the way
 * a form does. De-duped by start position (an sdt may also sit inside a range).
 */
function editableRegions(state: EditorState, ranges: RestrictedRange[]): { from: number; to: number }[] {
  const regions: { from: number; to: number }[] = ranges.map((r) => ({ from: r.from, to: r.to }));
  state.doc.descendants((node, pos) => {
    if (node.type.name === 'sdt' || node.type.name === 'blockSdt') {
      const from = pos + 1;
      const to = pos + node.nodeSize - 1;
      if (to > from && !regions.some((g) => g.from === from)) regions.push({ from, to });
    }
    // Cells marked editable are form fields too — Tab must land in them.
    if (
      (node.type.name === 'tableCell' || node.type.name === 'tableHeader') &&
      node.attrs.restrictedEditable === true
    ) {
      const from = pos + 1;
      const to = pos + node.nodeSize - 1;
      if (to > from && !regions.some((g) => g.from === from)) regions.push({ from, to });
    }
    return true;
  });
  return regions.sort((a, b) => a.from - b.from);
}

/**
 * Drop `propdocs:restricted` regions that hold no content.
 *
 * Deleting the last character inside an editable region leaves the wrapping
 * `sdt` behind, and an empty one is unreachable: it paints nothing, the caret
 * cannot enter it (ProseMirror normalises out of a zero-length inline node, so
 * typing lands beside it), `Select All` cannot cover it, and "Enable editing"
 * refuses an empty selection — so the toggle cannot unwrap it either. It stays
 * in the saved .docx forever and re-anchors text typed next to it as editable.
 *
 * Scoped to the restricted tag: checkboxes, placeholders and data-bound
 * controls may legitimately be empty and are left alone. Only inline `sdt` is
 * considered — `blockSdt` requires `block+`, so it can never be empty.
 */
function pruneEmptyRestrictedRegions(
  trs: readonly import('prosemirror-state').Transaction[],
  state: EditorState
): import('prosemirror-state').Transaction | null {
  if (!trs.some((t) => t.docChanged)) return null;

  const targets: number[] = [];
  state.doc.descendants((node, pos) => {
    if (node.type.name === 'sdt' && node.attrs.tag === RESTRICTED_TAG && node.content.size === 0) {
      targets.push(pos);
    }
    return true;
  });
  if (targets.length === 0) return null;

  const tr = state.tr;
  // Derived normalisation, not a user edit — the same marker the list-marker
  // normaliser uses, so the lock and the tracked-change catch-all skip it.
  tr.setMeta('propdocsDerived', true);
  tr.setMeta(restrictedKey, { type: 'suppressLock', value: true });
  // Delete last-first so an earlier removal can't shift a later position.
  for (const pos of targets.reverse()) {
    const node = tr.doc.nodeAt(pos);
    if (node?.type.name === 'sdt' && node.content.size === 0) {
      tr.delete(pos, pos + node.nodeSize);
    }
  }
  return tr.steps.length > 0 ? tr : null;
}

/**
 * Would this transaction destroy a FORM FIELD in a locked document?
 *
 * In a deal document the `propdocs:restricted` regions and placeholder controls
 * are the only places anyone may type, so the field itself is not the filler's
 * to remove — and `isInsideEditable` says yes to a range that is exactly a field
 * node, which is precisely a delete of the whole field.
 *
 * Three ways to lose one, all refused:
 *  - deleting the node;
 *  - EMPTYING it — an empty inline `sdt` paints nothing and the caret cannot
 *    re-enter it (see {@link pruneEmptyRestrictedRegions}), so clearing the last
 *    character loses the field for good;
 *  - marking its whole content deleted in SUGGESTING mode, which proposes the
 *    same loss and would be refused at accept time anyway — leaving a suggestion
 *    that could never be resolved.
 *
 * Replacing a selection — the ordinary form-fill gesture, delete + insert in ONE
 * transaction — leaves the field non-empty and is unaffected.
 */
function keepsFieldsIntact(tr: import('prosemirror-state').Transaction, state: EditorState): boolean {
  // Only a deletion or a deletion MARK can lose a field; a plain insertion or a
  // formatting mark never does, and those are the common case.
  let removesContent = false;
  tr.mapping.maps.forEach((map) => {
    map.forEach((oldStart, oldEnd) => {
      if (oldEnd > oldStart) removesContent = true;
    });
  });
  const marksDeleted = tr.steps.some(
    (step) => (step as unknown as { mark?: { type?: { name?: string } } }).mark?.type?.name === 'deletion'
  );
  if (!removesContent && !marksDeleted) return true;

  let intact = true;
  state.doc.descendants((node, pos) => {
    if (!intact) return false;
    if (!isFieldNode(node)) return true;
    const mapped = tr.mapping.mapResult(pos, 1);
    const after = mapped.deleted ? null : tr.doc.nodeAt(mapped.pos);
    if (!after || !isFieldNode(after)) {
      intact = false;
    } else if (node.content.size > 0 && after.content.size === 0) {
      intact = false;
    } else if (marksDeleted && isWhollyDeleted(after) && !isWhollyDeleted(node)) {
      intact = false;
    }
    // A field holds inline content only — nothing nested to check.
    return false;
  });
  return intact;
}

/** An editable region or a placeholder — the two fillable field constructs. */
function isFieldNode(node: PMNode): boolean {
  if (node.type.name !== 'sdt' && node.type.name !== 'blockSdt') return false;
  return node.attrs.tag === RESTRICTED_TAG || node.attrs.tag === PLACEHOLDER_SDT_TAG;
}

/** Is every bit of this field's content marked as a tracked deletion? */
function isWhollyDeleted(node: PMNode): boolean {
  if (node.content.size === 0) return false;
  let all = true;
  node.forEach((child) => {
    if (!child.marks.some((m) => m.type.name === 'deletion')) all = false;
  });
  return all;
}

export function restrictedEditingPlugin(
  /** Starting context. Header/footer EditorViews are created lazily and never
   *  receive the host's `setRestrictedContext` call, so they must be born
   *  locked — otherwise a restricted document still allowed free typing there. */
  initialContext: RestrictedContext = 'off'
): Plugin<RState> {
  return new Plugin<RState>({
    key: restrictedKey,
    state: {
      init: () => ({ context: initialContext, ranges: [] }),
      apply(tr, prev) {
        const meta = tr.getMeta(restrictedKey) as Meta | undefined;
        // map existing ranges across the transaction's changes
        let ranges = prev.ranges.map((r) => ({ from: tr.mapping.map(r.from), to: tr.mapping.map(r.to, -1) })).filter((r) => r.to > r.from);
        let context = prev.context;
        if (meta?.type === 'setContext') context = meta.context;
        if (meta?.type === 'addRange') ranges = [...ranges, { from: meta.from, to: meta.to }];
        if (meta?.type === 'toggleRange') {
          // If the selection overlaps any existing exception, REMOVE those
          // (make it non-editable again); otherwise ADD it. This makes "Enable
          // editing" a toggle.
          if (overlapsRange(ranges, meta.from, meta.to)) {
            ranges = ranges.filter((r) => !(meta.from < r.to && meta.to > r.from));
          } else {
            ranges = [...ranges, { from: meta.from, to: meta.to }];
          }
        }
        return { context, ranges };
      },
    },

    // THE LOCK: in locked context, reject any transaction that changes content
    // outside an editable region. Selection-only transactions always pass.
    filterTransaction(tr, state) {
      const s = restrictedKey.getState(state);
      if (!s || s.context !== 'locked') return true;
      if (!tr.docChanged) return true;
      // Authoring ops (wrap/unwrap an editable region) bypass the lock.
      if (tr.getMeta(restrictedKey)?.type === 'suppressLock') return true;
      // Derived normalizations (e.g. the list-marker normalizer re-stamping
      // attrs) are not user edits and must not be blocked, or numbering stops
      // updating inside a locked document.
      if (tr.getMeta('propdocsDerived')) return true;
      // Tracked run-FORMATTING changes are allowed anywhere, including locked
      // text. Striking a clause is a reviewable proposal, not an edit to the
      // protected wording: it changes no characters, carries a sidebar card, and
      // the owner can reject it. Blocking it left a reviewer unable to mark a
      // locked clause at all. Recognised by the transaction carrying ONLY
      // format-change steps (see `toggleTrackedMark`).
      if (isTrackedFormatOnly(tr, state)) return true;

      let ok = true;

      // Inspect STEPS directly. Mark steps (bold/italic/colour/highlight) and
      // attribute steps produce an EMPTY StepMap — positions don't move — so
      // the mapping-based scan below never saw them and every formatting
      // command silently applied to locked text. Ranges come off the step.
      for (const step of tr.steps) {
        const st = step as unknown as { from?: number; to?: number; pos?: number };
        if (typeof st.from === 'number' && typeof st.to === 'number') {
          if (!isInsideEditable(state, s.ranges, st.from, st.to)) return false;
        } else if (typeof st.pos === 'number') {
          if (!isInsideEditable(state, s.ranges, st.pos, st.pos + 1)) return false;
        }
      }

      // Replacements additionally report their ranges through the mapping.
      tr.mapping.maps.forEach((map) => {
        map.forEach((oldStart, oldEnd) => {
          if (!isInsideEditable(state, s.ranges, oldStart, oldEnd)) ok = false;
        });
      });
      if (!ok) return false;

      // A field's own range passes the test above — it IS an editable region —
      // so the checks so far happily allow deleting the field itself. The
      // sidebar's own writes (a placeholder deleted from the sidebar, a value
      // filled in) are exempt: those are not the user typing in the body.
      if (!tr.getMeta(PLACEHOLDER_WRITE_META) && !keepsFieldsIntact(tr, state)) return false;

      return true;
    },

    // Form-fill auto-select (locked/deal-doc context). The painter routes clicks
    // through a custom pointer that dispatches setSelection directly — it does
    // NOT invoke the `handleClick` prop — so selection expansion must happen
    // here, on the dispatched selection transaction. When the caret lands inside
    // a placeholder/content-control (or a blank underscore field) as a COLLAPSED
    // selection, expand it to the whole fillable region so typing REPLACES the
    // [label] instead of inserting into it.
    appendTransaction(trs, _oldState, newState) {
      const s = restrictedKey.getState(newState);
      if (!s) return null;
      if (s.context !== 'locked') {
        return pruneEmptyRestrictedRegions(trs, newState);
      }
      // Only react to selection-affecting transactions (clicks, arrow keys),
      // never to our own appended selection or to content edits (fills).
      if (!trs.some((t) => t.selectionSet && !t.docChanged)) return null;
      const sel = newState.selection;
      if (!sel.empty) return null; // already a range — leave it
      const pos = sel.from;

      // (1) Inside an UNFILLED placeholder (or a blank fill-in field) → select
      // its whole content so typing REPLACES the [label]. ONLY placeholders:
      // `propdocs:restricted` editable regions must behave like normal text
      // (caret lands where clicked, select/type freely) — expanding those made
      // whole regions replace-only. A placeholder that already holds a real
      // value also keeps normal caret behavior (edit, don't clobber).
      let region: { start: number; end: number } | null = null;
      newState.doc.nodesBetween(Math.max(0, pos - 1), Math.min(newState.doc.content.size, pos + 1), (node, nodePos) => {
        if ((node.type.name === 'sdt' || node.type.name === 'blockSdt') && pos >= nodePos && pos <= nodePos + node.nodeSize) {
          if (node.attrs.tag !== PLACEHOLDER_SDT_TAG) return true;
          const start = nodePos + 1;
          const end = nodePos + node.nodeSize - 1;
          if (end > start) {
            const text = node.textContent ?? '';
            const name = String(node.attrs.placeholder ?? node.attrs.alias ?? '');
            const unfilled =
              text.trim().length === 0 ||
              BLANK.test(text.replace(ZERO_WIDTH, '')) ||
              text === `[${name}]` ||
              /^\[.*\]$/.test(text.trim());
            if (unfilled) region = { start, end };
          }
        }
        return true;
      });

      // (2) Blank FILL-IN field → select the whole blank so typing replaces it.
      // This is the underscores/spaces case ("____" or a run of spaces): those
      // are form blanks, so one click selects the lot. Covers BOTH a legacy
      // plugin-state exception range AND a `propdocs:restricted` SDT (the
      // persistent form of an editable region) — checking only the former
      // silently dropped the behaviour on real restricted docs, whose blanks
      // live inside restricted SDTs. Non-blank restricted text is left alone
      // so it edits like normal prose.
      if (!region) {
        const isBlank = (from: number, to: number): boolean => {
          if (to <= from) return false;
          const text = newState.doc.textBetween(from, to, '').replace(ZERO_WIDTH, '');
          return text.length > 0 && BLANK.test(text);
        };
        const range = s.ranges.find((r) => pos >= r.from && pos <= r.to);
        if (range && isBlank(range.from, range.to)) {
          region = { start: range.from, end: range.to };
        } else {
          // Walk up to an enclosing `propdocs:restricted` SDT and test its text.
          const $pos = newState.doc.resolve(Math.min(Math.max(pos, 0), newState.doc.content.size));
          for (let d = $pos.depth; d >= 0 && !region; d--) {
            const node = $pos.node(d);
            if (
              (node.type.name === 'sdt' || node.type.name === 'blockSdt') &&
              node.attrs.tag === RESTRICTED_TAG
            ) {
              const start = (d === 0 ? 0 : $pos.before(d)) + 1;
              const end = start + node.content.size;
              if (isBlank(start, end)) region = { start, end };
            }
          }
        }
      }

      if (!region) return null;
      const { start, end } = region;
      // Guard: don't re-expand if already exactly this range (avoid loops).
      if (sel.from === start && sel.to === end) return null;
      return newState.tr.setSelection(TextSelection.create(newState.doc, start, end));
    },

    props: {
      // Decorate exception ranges so the user sees where they can edit.
      decorations(state) {
        const s = restrictedKey.getState(state);
        if (!s || s.context === 'off') return null;
        const decos = s.ranges.map((r) =>
          Decoration.inline(r.from, r.to, { class: 'restricted-editing-exception' }),
        );
        // Persistent exceptions are SDT NODES, not plugin-state ranges, so
        // decorating only `s.ranges` left every saved region undecorated.
        state.doc.descendants((node, pos) => {
          if (
            (node.type.name === 'tableCell' || node.type.name === 'tableHeader') &&
            node.attrs.restrictedEditable === true
          ) {
            decos.push(
              Decoration.node(pos, pos + node.nodeSize, { class: 'restricted-editing-exception' })
            );
          }
          if (
            (node.type.name === 'sdt' || node.type.name === 'blockSdt') &&
            node.attrs.tag === RESTRICTED_TAG
          ) {
            const from = pos + 1;
            const to = pos + node.nodeSize - 1;
            if (to > from) {
              decos.push(Decoration.inline(from, to, { class: 'restricted-editing-exception' }));
            }
          }
          return true;
        });
        return DecorationSet.create(state.doc, decos);
      },

      // Typing at the TRAILING edge of an editable region must not join it.
      //
      // The painter maps every click at or past a region's right edge to the
      // position INSIDE the region (its content end) — there is no reachable
      // caret position just after one — so text meant to follow a region was
      // absorbed into it and silently became editable in the deal documents
      // made from this template. Re-issuing the insertion one position later
      // puts it in the paragraph: ProseMirror's own transform places an
      // inline slice at that boundary OUTSIDE the node, it is only the
      // DOM-derived input path that lands inside.
      //
      // Skipped in LOCKED (deal-doc) context, where appending at the end of a
      // field is the normal form-filling gesture and the text belongs inside.
      handleTextInput(view, from, to, text) {
        const s = restrictedKey.getState(view.state);
        if (!s || s.context === 'locked') return false;
        if (from !== to) return false; // replacing a range — leave it alone
        const $from = view.state.doc.resolve(from);
        const region = $from.parent;
        if (region.type.name !== 'sdt' || region.attrs.tag !== RESTRICTED_TAG) return false;
        if ($from.parentOffset !== region.content.size) return false; // not at the end

        const after = $from.after($from.depth);
        // Keep the run formatting the caret already carries, so text following a
        // region matches what the author was typing rather than paragraph defaults.
        const marks = view.state.storedMarks ?? $from.marks();
        const tr = view.state.tr.insert(after, view.state.schema.text(text, marks));
        tr.setSelection(TextSelection.create(tr.doc, after + text.length));
        view.dispatch(tr.scrollIntoView());
        return true;
      },

      // Tab / Shift-Tab jump between editable regions (like a form), rather than
      // inserting a tab. Only in LOCKED (deal-doc) context; in template/off Tab
      // keeps its normal behavior. Runs before the extension keymaps (a plugin's
      // handleKeyDown fires ahead of keymap plugins), so it preempts insertTab.
      handleKeyDown(view, event) {
        if (event.key !== 'Tab') return false;
        const s = restrictedKey.getState(view.state);
        if (!s || s.context !== 'locked') return false;
        const regions = editableRegions(view.state, s.ranges);
        if (regions.length === 0) return false;
        event.preventDefault();

        const { from } = view.state.selection;
        let target: { from: number; to: number };
        if (event.shiftKey) {
          // previous region whose end is before the caret, else wrap to last
          const prev = [...regions].reverse().find((g) => g.to < from);
          target = prev ?? regions[regions.length - 1];
        } else {
          // next region whose start is at/after the caret's region end, else wrap
          const next = regions.find((g) => g.from > from);
          target = next ?? regions[0];
        }
        const tr = view.state.tr.setSelection(
          TextSelection.create(view.state.doc, target.from, target.to),
        );
        view.dispatch(tr.scrollIntoView());
        view.focus();
        return true;
      },
    },
  });
}

// ── commands / helpers the workbench UI calls ───────────────────────────────

export function setRestrictedContext(view: EditorView, context: RestrictedContext) {
  view.dispatch(view.state.tr.setMeta(restrictedKey, { type: 'setContext', context }));
}

/** Tag identifying an editable-exception content control (matches the migration
 *  converter's `RESTRICTED_TAG`). */
export const RESTRICTED_TAG = 'propdocs:restricted';

/**
 * The `propdocs:restricted` SDT node enclosing `pos`, if any (walks up the
 * resolved position). Returns its start position + node, or null.
 */
function restrictedSdtAt(state: EditorState, pos: number): { pos: number; node: PMNode } | null {
  const $pos = state.doc.resolve(Math.min(Math.max(pos, 0), state.doc.content.size));
  for (let d = $pos.depth; d >= 0; d--) {
    const node = $pos.node(d);
    if ((node.type.name === 'sdt' || node.type.name === 'blockSdt') && node.attrs.tag === RESTRICTED_TAG) {
      return { pos: d === 0 ? 0 : $pos.before(d), node };
    }
  }
  return null;
}

/** Depth of the nearest enclosing textblock (the paragraph), or -1. */
function textblockDepth($pos: import('prosemirror-model').ResolvedPos): number {
  for (let d = $pos.depth; d >= 0; d--) {
    if ($pos.node(d).isTextblock) return d;
  }
  return -1;
}

/**
 * Outer bounds of the inline content control containing `[from, to]`, or null
 * when the range isn't wholly inside one.
 *
 * A checkbox is an inline `sdt` holding its glyph, so selecting the glyph puts
 * the range inside the control rather than around it. Marking a fragment of a
 * control editable is never what the user meant — they pointed at the control.
 */
function enclosingInlineControl(
  state: EditorState,
  from: number,
  to: number
): { from: number; to: number } | null {
  const $from = state.doc.resolve(from);
  for (let d = $from.depth; d > 0; d--) {
    const node = $from.node(d);
    if (node.type.name !== 'sdt') continue;
    // Already the restricted wrapper — the unwrap branch above owns that case.
    if (node.attrs.tag === RESTRICTED_TAG) return null;
    const start = $from.before(d);
    const end = start + node.nodeSize;
    if (to <= end) return { from: start, to: end };
  }
  return null;
}

/**
 * "Make editable" — TOGGLE, and now a REAL document change (so it autosaves and
 * survives reload, unlike the old plugin-state-only range).
 *
 * - If the selection sits inside an existing `propdocs:restricted` SDT → UNWRAP
 *   it (lift the content out, remove the control) = make non-editable again.
 * - Otherwise → WRAP the selection in a `propdocs:restricted` SDT:
 *     • selection within one text block → inline `sdt` (content: inline*)
 *     • selection spanning blocks       → `blockSdt` (content: block+)
 *   The restricted plugin treats any SDT as an editable island, so this both
 *   marks the region editable AND persists it in the .docx body.
 */
export function makeSelectionEditable(view: EditorView): boolean {
  const { state } = view;
  const { from, to, empty } = state.selection;
  if (empty) return false;

  // Already inside a restricted SDT (either end) → unwrap = toggle off.
  const existing = restrictedSdtAt(state, from) ?? restrictedSdtAt(state, to);
  if (existing) {
    const tr = state.tr.setMeta(restrictedKey, { type: 'suppressLock', value: true });
    const start = existing.pos;
    const inner = existing.node.content;
    // Replace the whole SDT node with its inner content (unwrap).
    tr.replaceWith(start, start + existing.node.nodeSize, inner);
    view.dispatch(tr.scrollIntoView());
    return true;
  }

  // Selection SURROUNDS whole regions → unwrap those. `restrictedSdtAt` only
  // walks UP, so it recognises a region containing the caret but never one the
  // selection contains — and wrapping leaves the selection around the new node,
  // which is exactly that shape. The immediate second click therefore missed
  // the unwrap branch, fell through to the wrap below, and NESTED a region
  // inside itself while still reporting success, so the highlight could never
  // be switched off. Only top-level hits (no descending) so an outer and its
  // inner are never spliced in one pass; peeling one layer per click keeps
  // already-nested content recoverable.
  const surrounded: { pos: number; node: PMNode }[] = [];
  state.doc.nodesBetween(from, to, (node, pos) => {
    const isRegion =
      (node.type.name === 'sdt' || node.type.name === 'blockSdt') &&
      node.attrs.tag === RESTRICTED_TAG;
    if (isRegion && pos >= from && pos + node.nodeSize <= to) {
      surrounded.push({ pos, node });
      return false;
    }
    return true;
  });
  if (surrounded.length > 0) {
    const tr = state.tr.setMeta(restrictedKey, { type: 'suppressLock', value: true });
    // Last-first so an earlier splice can't shift a later position.
    for (const { pos, node } of surrounded.reverse()) {
      tr.replaceWith(pos, pos + node.nodeSize, node.content);
    }
    view.dispatch(tr.scrollIntoView());
    return true;
  }

  const sdtType = state.schema.nodes.sdt;
  const blockSdtType = state.schema.nodes.blockSdt;

  // A CELL SELECTION is not a range — its `from`/`to` are cell-boundary
  // positions, so the generic path treated them as a block range and sliced
  // ACROSS the grid: selecting one cell of a 2x2 produced three rows and
  // orphaned its neighbour. Each selected cell has its CONTENT wrapped
  // instead, so the grid is never restructured.
  if (state.selection instanceof CellSelection) {
    const cells: { pos: number; node: PMNode }[] = [];
    state.selection.forEachCell((node, pos) => cells.push({ pos, node }));
    if (cells.length === 0) return false;

    // Mark the CELL, never its contents. Wrapping content in an SDT cannot work
    // here: `tableCell` is `(paragraph | table)+` so a `blockSdt` is rejected
    // and ProseMirror restructures the grid to fit it, an empty cell has
    // nothing to wrap, and a wrapper tied to the text vanishes when the user
    // clears it. An attr on the container survives all three.
    const next = !cells.every(({ node }) => node.attrs.restrictedEditable === true);
    const tr = state.tr.setMeta(restrictedKey, { type: 'suppressLock', value: true });
    for (const { pos, node } of cells) {
      if (node.attrs.restrictedEditable === next) continue;
      tr.setNodeMarkup(pos, undefined, { ...node.attrs, restrictedEditable: next });
    }
    if (tr.steps.length === 0) return false;
    view.dispatch(tr.scrollIntoView());
    return true;
  }

  // Highlighting a checkbox lands the selection INSIDE that control's own sdt,
  // whose content is `inline*`. Expanding to the control first means any part of
  // it — the glyph, the whole node, a drag that clips it — marks the same thing,
  // instead of wrapping a fragment of a control.
  const controlRange = enclosingInlineControl(state, from, to);
  const wrapFrom = controlRange?.from ?? from;
  const wrapTo = controlRange?.to ?? to;

  const $from = state.doc.resolve(wrapFrom);
  const $to = state.doc.resolve(wrapTo);
  // Resolve to the enclosing TEXTBLOCK, not just the immediate parent. An inline
  // sdt is a parent but not a textblock, so `$from.parent.isTextblock` was false
  // for a selection inside a control and the block path took over — and
  // `$from.before($from.depth)` there points at the control itself, so it hoisted
  // the checkbox into a top-level blockSdt and split the paragraph around it,
  // leaving the remaining text as a new numbered item.
  const fromBlock = textblockDepth($from);
  const toBlock = textblockDepth($to);
  const sameTextBlock =
    fromBlock >= 0 && toBlock >= 0 && $from.start(fromBlock) === $to.start(toBlock);

  const tr = state.tr.setMeta(restrictedKey, { type: 'suppressLock', value: true });
  const attrs = { tag: RESTRICTED_TAG, lock: 'unlocked' };

  try {
    if (sameTextBlock && sdtType) {
      // Inline wrap: pull the selected inline slice into an inline sdt.
      const slice = state.doc.slice(wrapFrom, wrapTo);
      const inlineContent = slice.content; // inline fragment
      const sdtNode = sdtType.create(attrs, inlineContent);
      tr.replaceRangeWith(wrapFrom, wrapTo, sdtNode);
    } else if (blockSdtType) {
      // Block wrap: expand to whole enclosing blocks, wrap them in a blockSdt.
      const blockFrom = $from.before($from.depth);
      const blockTo = $to.after($to.depth);
      const slice = state.doc.slice(blockFrom, blockTo);
      const blockNode = blockSdtType.create(attrs, slice.content);
      tr.replaceRangeWith(blockFrom, blockTo, blockNode);
    } else {
      return false;
    }
  } catch {
    return false; // schema wouldn't accept the wrap → no-op
  }

  view.dispatch(tr.scrollIntoView());
  return true;
}

export function getRestrictedState(view: EditorView | null): RState | null {
  return view ? restrictedKey.getState(view.state) ?? null : null;
}

/**
 * Can content be INSERTED at the current selection? Drives the deal placeholder
 * sidebar "+" and the Insert menu's Placeholder/Checkbox items: on a restricted
 * (locked) deal doc you may only insert inside an editable region (an exception
 * range or a content-control), so the affordances disable when the cursor sits
 * in the locked body. When the doc isn't locked ('off'/'template'), always true.
 *
 * Uses the SAME `isInsideEditable` test the lock's `filterTransaction` uses, so
 * the affordance state exactly matches what the editor would actually allow — a
 * collapsed caret at `from` is treated as an insertion point (from === to).
 */
export function isCursorInsertable(view: EditorView | null): boolean {
  if (!view) return false;
  const s = getRestrictedState(view);
  if (!s || s.context !== 'locked') return true;
  const { from, to } = view.state.selection;
  return isInsideEditable(view.state, s.ranges, from, to);
}

/** Is [from,to] currently marked editable — i.e. inside a `propdocs:restricted`
 *  SDT (the persistent exception) OR a legacy plugin-state range? Drives the
 *  Enable ↔ Disable label on the floating "make editable" bubble. */
export function isRangeEditable(view: EditorView | null, from: number, to: number): boolean {
  if (!view) return false;
  const state = getRestrictedState(view);
  if (state && overlapsRange(state.ranges, from, to)) return true;
  // Persistent exceptions are `propdocs:restricted` SDT nodes.
  if (restrictedSdtAt(view.state, from) !== null || restrictedSdtAt(view.state, to) !== null) {
    return true;
  }
  // Walking UP from the endpoints misses a cell selection: its `from`/`to` sit
  // at cell boundaries, OUTSIDE the wrapper that lives inside the cell's
  // paragraph. Scan the range itself so marked cells report as editable — and
  // so the bubble offers "Disable editing" instead of silently re-marking them.
  let found = false;
  view.state.doc.nodesBetween(from, Math.max(from, to), (node) => {
    if (
      ((node.type.name === 'sdt' || node.type.name === 'blockSdt') &&
        node.attrs.tag === RESTRICTED_TAG) ||
      ((node.type.name === 'tableCell' || node.type.name === 'tableHeader') &&
        node.attrs.restrictedEditable === true)
    ) {
      found = true;
    }
    return !found;
  });
  return found;
}
